class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    document.dispatchEvent(new CustomEvent('fuji:overlay-open', { detail: { source: this } }));
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');
    requestAnimationFrame(() => {
      if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close(restoreFocus = true) {
    this.classList.remove('active');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(restoreFocus ? this.activeElement : null);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const replacements = this.getSectionsToRender().map(section => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const markup = parsedState.sections?.[section.id];
      const source = typeof markup === 'string'
        ? new DOMParser().parseFromString(markup, 'text/html').querySelector(section.selector || '.shopify-section')
        : null;
      return { target, source };
    });
    if (replacements.some(replacement => !replacement.target || !replacement.source)) {
      this.navigateToCart();
      return;
    }
    replacements.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
      } else {
        this.open();
      }
    });
  }

  navigateToCart() {
    window.location.assign(routes.cart_url);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainItems && mainFooter) sections.push(
      { id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }
    );
    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
